import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useT, useResources } from '~app/core/BundleProvider';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as DocumentActions } from '~app/features/document';
import SideBarButton from './SideBarButton';

//import Logging from "~app/core/logging";
//const log = Logging.getLogger("app.components.sidebar.toolbar");

const ToolBar = () => {
  const t = useT();
  const r = useResources();
  const dispatch = useDispatch();

  const documentType = useSelector(state => state.document.present?.type);
  const selection = useSelector(state => state.canvas.selection, shallowEqual);
  const selectedNode = useSelector(
    state => state.document.present?.workingModel?.nodes?.[selection.label],
    shallowEqual,
  );
  let editMode = useSelector(state => state.document.present?.editMode);
  const defaultEditMode = useSelector(state => state.canvas.defaultEditMode);
  if (!editMode) {
    editMode = defaultEditMode;
  }
  const isFlowSelected =
    selection.type === 'node' && selectedNode?.type === 'flow';
  const isFlowSourceSelected = selection?.type === 'flowTail';
  const isFlowSinkSelected = selection?.type === 'flowHead';
  const inputIsLatched =
    (isFlowSelected || isFlowSourceSelected) &&
    selection.latched &&
    selection.latchData.direction === 'from';
  const outputIsLatched =
    (isFlowSelected || isFlowSinkSelected) &&
    selection.latched &&
    selection.latchData.direction === 'to';

  const isSignalSrc =
    selection.type === 'node' &&
    (selectedNode?.type === 'stock' ||
      selectedNode?.type === 'variable' ||
      selectedNode?.type === 'flow' ||
      selectedNode?.type === 'generic');

  const isInCompareMode = useSelector(
    state => state.canvas?.compareWithSolution ?? false,
  );
  const missingObjectsCount = useSelector(
    state =>
      state.document.present?.workingModelErrors?.missingObjectsCount ?? 0,
  );
  const showMissingObjectsMarker = isInCompareMode && missingObjectsCount > 0;

  const signalClick = useCallback(
    (selection, polarity) => {
      if (selection.type === 'signal') {
        const [fromLabel, toLabel] = selection.label.split('>');
        dispatch(DocumentActions.addSignal({ polarity, fromLabel, toLabel }));
      } else {
        dispatch(
          CanvasActions.latchForAction({ action: 'addSignal', polarity }),
        );
      }
    },
    [dispatch],
  );
  const signalLatch =
    isSignalSrc && selection.latched && selection.latchData.polarity;

  const changeLink = useCallback(
    mode => {
      let dir = mode === 'input' ? 'From' : 'To';
      const link = selectedNode?.['connect' + dir];
      dir = dir.toLowerCase();
      if (link) {
        // link exists, so disconnect it
        dispatch(
          DocumentActions.connectFlow({
            flowLabel: selection.label,
            [dir + 'Label']: null,
          }),
        );
      } else {
        dispatch(
          CanvasActions.latchForAction({
            action: 'connectFlow',
            direction: dir,
          }),
        );
      }
    },
    [dispatch, selectedNode, selection.label],
  );

  const labelBankExpanded = useSelector(
    state => state.canvas.labelBankExpanded,
  );

  const showHideLabelBank = useCallback(() => {
    if (labelBankExpanded === true) {
      dispatch(CanvasActions.collapseLabelBank());
    } else {
      dispatch(CanvasActions.expandLabelBank());
    }
  }, [labelBankExpanded, dispatch]);

  const createLabel = useCallback(() => {
    dispatch(DocumentActions.createLabel());
  }, [dispatch]);

  const flowFromButtonText = t(
    inputIsLatched
      ? 'flowButtonLinkable-from'
      : selectedNode?.connectFrom && !isFlowSinkSelected
      ? 'flowButtonUnlinkable-from'
      : 'flowButtonDefault-from',
  );
  const flowToButtonText = t(
    outputIsLatched
      ? 'flowButtonLinkable-to'
      : selectedNode?.connectTo && !isFlowSourceSelected
      ? 'flowButtonUnlinkable-to'
      : 'flowButtonDefault-to',
  );

  const handleBankToggleKeyPress = e => {
    if ((e.key === 'Enter' || e.key === ' ') && editMode !== 'solution') {
      showHideLabelBank();
    }
  };

  useEffect(() => {
    if (isFlowSinkSelected) {
      changeLink('output');
    }
    if (isFlowSourceSelected) {
      changeLink('input');
    }
  }, [changeLink, isFlowSinkSelected, isFlowSourceSelected]);

  return (
    <div className="ToolBar__container">
      {editMode === 'solution' ? (
        <h3 className="ToolBar__header mb-0">{t('add-label')}</h3>
      ) : (
        <div
          className={`ToolBar__header ToolBar__header-clickable
          ${labelBankExpanded ? 'ToolBar__header-expanded' : ''}`}
          onClick={showHideLabelBank}
          onKeyPress={handleBankToggleKeyPress}
          tabIndex="0"
          role="button"
          aria-expanded={labelBankExpanded}
          aria-controls="label-bank-container"
        >
          {showMissingObjectsMarker && (
            <div className="label__bank__errormark"></div>
          )}
          {t('label-bank')} <span> &raquo;</span>{' '}
        </div>
      )}

      {editMode === 'solution' && (
        <SideBarButton
          src={r('add-icon.svg')}
          ariaLabel={t('add-label')}
          onClick={createLabel}
          alt="add"
        />
      )}
      {documentType === 'stockflow' && (
        <>
          <div className="ToolBar__divider" />
          <h3 className="ToolBar__header">{t('object-types')}</h3>
          <div role="list">
            {editMode === 'solution' && (
              <div role="listitem">
                <SideBarButton
                  symbol="UnassignedIcon"
                  ariaLabel={t('unassigned')}
                  onClick={() =>
                    dispatch(
                      DocumentActions.assignNodeType({
                        label: selection.label,
                        type: 'unassigned',
                      }),
                    )
                  }
                  hotkey="u"
                  disabled={selection.type !== 'node'}
                >
                  {t('unassigned')}
                </SideBarButton>
              </div>
            )}
            <div role="listitem">
              <SideBarButton
                symbol="StockIcon"
                ariaLabel={t('stock')}
                onClick={() =>
                  dispatch(
                    DocumentActions.assignNodeType({
                      label: selection.label,
                      type: 'stock',
                    }),
                  )
                }
                hotkey="s"
                disabled={selection.type !== 'node'}
              >
                {t('stock')}
              </SideBarButton>
            </div>
            <div role="listitem">
              <SideBarButton
                symbol="VariableIcon"
                ariaLabel={t('variable')}
                onClick={() =>
                  dispatch(
                    DocumentActions.assignNodeType({
                      label: selection.label,
                      type: 'variable',
                    }),
                  )
                }
                hotkey="v"
                disabled={selection.type !== 'node'}
              >
                {t('variable')}
              </SideBarButton>
            </div>
          </div>
          <div className="ToolBar__flowButtons" role="list">
            <div role="listitem">
              <SideBarButton
                symbol="FlowIcon"
                ariaLabel={t('flow')}
                onClick={() =>
                  dispatch(
                    DocumentActions.assignNodeType({
                      label: selection.label,
                      type: 'flow',
                    }),
                  )
                }
                hotkey="f"
                disabled={selection.type !== 'node'}
              >
                {t('flow')}
              </SideBarButton>
            </div>
            <div role="listitem">
              <SideBarButton
                ariaLabel={flowFromButtonText}
                onClick={() => changeLink('input')}
                disabled={!isFlowSelected && !isFlowSourceSelected}
                hotkey="i"
              >
                <div
                  className={`SideBarButton__border ${
                    inputIsLatched ? 'hilited' : ''
                  }`}
                >
                  {flowFromButtonText}
                </div>
              </SideBarButton>
            </div>
            <div role="listitem">
              <SideBarButton
                ariaLabel={flowToButtonText}
                onClick={() => changeLink('output')}
                disabled={!isFlowSelected && !isFlowSinkSelected}
                hotkey="o"
              >
                <div
                  className={`SideBarButton__border ${
                    outputIsLatched ? 'hilited' : ''
                  }`}
                >
                  {flowToButtonText}
                </div>
              </SideBarButton>
            </div>
          </div>
        </>
      )}
      <div className="ToolBar__divider" />
      <h3 className="ToolBar__header">{t('signal-types')}</h3>
      <div role="list">
        {documentType !== 'conceptual' && (
          <>
            <div role="listitem">
              <SideBarButton
                symbol="ConnectorPositiveIcon"
                ariaLabel={t('positive')}
                onClick={() => signalClick(selection, '+')}
                disabled={selection.type !== 'signal' && !isSignalSrc}
                hilited={signalLatch === '+'}
                hotkey="p"
              >
                {t('positive')}
              </SideBarButton>
            </div>
            <div role="listitem">
              <SideBarButton
                symbol="ConnectorNegativeIcon"
                ariaLabel={t('negative')}
                onClick={() => signalClick(selection, '-')}
                disabled={selection.type !== 'signal' && !isSignalSrc}
                hilited={signalLatch === '-'}
                hotkey="n"
              >
                {t('negative')}
              </SideBarButton>
            </div>
          </>
        )}
        {documentType !== 'causal' && documentType !== 'stockflow' && (
          <div role="listitem">
            <SideBarButton
              symbol="ConnectorNeutralIcon"
              ariaLabel={t('neutral')}
              onClick={() => signalClick(selection, '0')}
              disabled={selection.type !== 'signal' && !isSignalSrc}
              hilited={signalLatch === '0'}
              hotkey="0"
            >
              {t('neutral')}
            </SideBarButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default ToolBar;
